import { Link } from 'react-router-dom';
import type { Exam, LearningFocus } from '@mastery/api-client-web';
import { ArrowRight, Target } from './Icons';
import { percent } from '../lib/shared';
import { focusTitle, focusURL, trainingURL } from '../lib/learning';

export const focusStates: Record<string, string> = {
  building: '积累样本',
  needs_practice: '优先加强',
  steady: '继续巩固',
  strength: '表现较好',
};
export const interpretationLabels: Record<string, string> = {
  part: '更偏向题型薄弱',
  topic: '更偏向主题薄弱',
  intersection: '题型与主题组合薄弱',
  insufficient: '还需更多对照',
  mixed: '继续观察差异',
};

export function FocusCard({ focus, exams }: { focus: LearningFocus; exams: Exam[] }) {
  return (
    <article className={'focus-card ' + focus.state}>
      <div className="focus-card-top">
        <span>
          {focus.dimension === 'part'
            ? '题型维度'
            : focus.dimension === 'topic'
              ? '内容维度'
              : '交叉观察'}
        </span>
        <span className="focus-status">{focusStates[focus.state]}</span>
      </div>
      <h3>{focusTitle(focus, exams)}</h3>
      <div className="focus-score">
        <strong>{percent(focus.accuracy)}</strong>
        <span>首次作答正确率</span>
      </div>
      <div className="focus-bar">
        <i style={{ width: percent(focus.accuracy) }} />
      </div>
      <p>{focus.reason}</p>
      <small>
        {focus.total} 道新题 · {focus.groups} 组材料 · 平均难度 {focus.difficulty.toFixed(1)}
      </small>
      <Link to={trainingURL(focus)}>
        {focus.state === 'building'
          ? '补充同类新题'
          : focus.state === 'strength'
            ? '继续巩固这项优势'
            : '针对这项练习'}
        <ArrowRight size={17} />
      </Link>
    </article>
  );
}

export function ProfileMatrix({ profile, exams }: { profile: LearningFocus[]; exams: Exam[] }) {
  const cells = profile.filter((f) => f.dimension === 'intersection');
  const topics = [...new Set(cells.map((f) => f.topic))].sort((a, b) =>
    a.localeCompare(b, 'zh-CN'),
  );
  const parts = [
    ...new Map(cells.map((f) => [`${f.exam_code}:${f.section}:${f.part}`, f])).values(),
  ];
  parts.sort((a, b) => {
    const sections = exams.find((e) => e.code === a.exam_code)?.sections ?? [];
    return (
      a.exam_code.localeCompare(b.exam_code) ||
      sections.findIndex((s) => s.code === a.section) -
        sections.findIndex((s) => s.code === b.section) ||
      a.part - b.part
    );
  });
  if (!cells.length)
    return (
      <div className="profile-empty">
        <Target size={30} weight="duotone" />
        <h3>每一次作答，都让诊断更清楚。</h3>
        <p>完成阅读或听力练习后，这里会逐步呈现题型与内容主题的交叉表现。</p>
      </div>
    );
  return (
    <div className="matrix-scroll" tabIndex={0} aria-label="题型与内容主题交叉表现，可横向滚动">
      <table className="profile-matrix">
        <thead>
          <tr>
            <th scope="col">题型 / 内容主题</th>
            {topics.map((t) => (
              <th scope="col" key={t}>
                {t}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {parts.map((p) => (
            <tr key={`${p.exam_code}:${p.section}:${p.part}`}>
              <th scope="row">{focusTitle({ ...p, topic: '' }, exams)}</th>
              {topics.map((topic) => {
                const f = cells.find(
                  (f) =>
                    f.exam_code === p.exam_code &&
                    f.section === p.section &&
                    f.part === p.part &&
                    f.topic === topic,
                );
                return (
                  <td key={topic}>
                    {f ? (
                      <Link
                        className={'matrix-cell ' + f.state}
                        to={focusURL(f)}
                        aria-label={`${focusTitle(f, exams)}，${focusStates[f.state]}，${percent(f.accuracy)}，${f.total} 道首次作答`}
                      >
                        <strong>{percent(f.accuracy)}</strong>
                        <small>
                          {focusStates[f.state]} · {f.total} 题
                        </small>
                      </Link>
                    ) : (
                      <span className="matrix-missing">尚未练习</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
